import 'cropperjs/dist/cropper.css';

import React, { useEffect, useRef, useState } from 'react';
import Cropper from 'react-cropper';
import { useSearchParams } from 'react-router-dom';
import { CheckIcon, CloseIcon } from 'tdesign-icons-react';
import { Button, Image, Loading, MessagePlugin } from 'tdesign-react';

import { BasicImageViewer } from '@/components/review/image';
import { base64ToFile } from '@/utils/file';

import { uploadCosImage } from '../form-upload/service';
import styles from './index.module.less';

interface ImageItem {
  file: File;
  data?: string;
  width?: number;
  height?: number;
}

interface CropViewerProps {
  imageItem?: ImageItem;
  showPreview?: boolean;
  cropperBoxMinWidth?: number;
  cropperBoxMinHeight?: number;
  cropContainerWidth?: number;
  cropContainerHeight?: number;
  cropRatio?: number;
  cropperProps?: Cropper.Options<HTMLImageElement> &
  Omit<React.HTMLProps<HTMLImageElement>, 'data' | 'ref' | 'crossOrigin'>;
  name?: string;
  isHorizontal?: boolean;
  onCancel: () => void;
  onConfirm: (value: string) => void;
}

const CropViewer: React.FC<CropViewerProps> = ({
  imageItem,
  showPreview = true,
  cropperBoxMinWidth = 200,
  cropperBoxMinHeight = 200,
  cropContainerWidth = 360,
  cropContainerHeight = 360,
  cropRatio,
  cropperProps,
  name,
  isHorizontal = false,
  onCancel,
  onConfirm,
}) => {
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('id') || '';
  const [croppedImgData, setCroppedImgData] = useState<string>();
  const [processing, setProcessing] = useState(true);
  const [cropLoading, setCropLoading] = useState(false);
  const cropperRef = useRef<any>();
  const cropFirstRef = useRef(false);

  const onCrop = () => {
    if (cropFirstRef.current) {
      return;
    }
    onCropEnd();
    cropFirstRef.current = true;
  };

  const onCropEnd = () => {
    if (!showPreview) return;
    const data = cropperRef?.current?.cropper?.getCroppedCanvas?.()?.toDataURL();
    setCroppedImgData(data);
    setProcessing(false);
  };

  const handleConfirm = async () => {
    setCropLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    onCropEnd();
    if (croppedImgData) {
      const file = base64ToFile(croppedImgData, name || '');
      const res = await uploadCosImage(file, { storyId });
      const url = res?.response?.url;
      setCropLoading(false);
      onConfirm(url);
    } else {
      MessagePlugin.error('裁剪失败，请点击重试！');
    }
  };

  useEffect(() => {
    const cropper = cropperRef?.current?.cropper;
    if (!cropper) return;
    setProcessing(true);
    cropper.options = {
      ...cropper.options,
      minCropBoxWidth: imageItem?.width ? (cropperBoxMinWidth * cropContainerWidth) / imageItem?.width : 100,
      minCropBoxHeight: imageItem?.height ? (cropperBoxMinHeight * cropContainerHeight) / imageItem?.height : 100,
    };
    cropper
      .reset()
      .clear()
      .replace(imageItem?.data || '');
  }, [imageItem, cropperRef]);

  const imageStyle = isHorizontal
    ? { width: 160, height: 74 } // 横图比例 812:375
    : { width: 90, height: 160 };

  return (
    <Loading
      loading={processing || cropLoading}
      style={{ width: '100%', height: '100%' }}
    >
      <div className={styles.wrapper}>
        <Cropper
          ref={cropperRef}
          guides={false}
          movable={true}
          rotatable={false}
          scalable={false}
          zoomable={true}
          background={true}
          responsive={true}
          autoCropArea={1}
          aspectRatio={cropRatio}
          viewMode={0}
          dragMode='move'
          {...cropperProps}
          className={styles.cropper}
          style={{ ...cropperProps?.style, width: cropContainerWidth, height: cropContainerHeight }}
          ready={() => setTimeout(() => setProcessing(false), 1000)}
          crop={onCrop}
          cropend={onCropEnd}
        />
        {showPreview && croppedImgData
          && <div className={styles.preview}>
            <div className={styles.title}>
              <span>预览图</span>
            </div>
            <BasicImageViewer images={[croppedImgData]}>
              <Image
                src={croppedImgData}
                fit='contain'
                style={imageStyle}
              />
            </BasicImageViewer>
            <div className={styles.btnGroup}>
              <Button
                theme='default'
                style={{ width: 32, height: 32 }}
                onClick={onCancel}
              >
                <CloseIcon size={16} />
              </Button>
              <Button
                theme='primary'
                style={{ width: 32, height: 32 }}
                onClick={handleConfirm}
              >
                <CheckIcon size={16} />
              </Button>
            </div>
          </div>}
      </div>
    </Loading>
  );
};

export default CropViewer;
